import { useState } from 'react';
import { DialogType } from '../../../common/enums.js';
import { billingAccountAndUserMsg, editMsg, t } from '../../../common/i18n/index.js';
import { isExternalSubscription } from '../../../common/utils/subscriptionUtils.js';
import { SubscriptionStatusType } from '../../../generated/api/subscriptionStatusType.js';
import { SubscriptionType } from '../../../generated/api/subscriptionType.js';
import { useAuth } from '../../../public/site/AuthProvider.js';
import { openDialog } from '../../../public/site/dialogStore.js';
import { HeaderWithEditButton } from '../../HeaderWithButton/HeaderWithButton.js';
import {
  subscriptionActionsPendingDialog,
  subscriptionInActivationDialog,
  subscriptionSuspendedDialog,
} from '../../UserAndNumberPublicity/UserNumberPublicityUtils.js';
import { useScheduledChange } from '../hooks/useScheduledChange';
import { createPurposeOfUseObj } from '../subscriptionDetailsUtils.js';
import { BillingAccountAndUserInfoEditForm } from './BillingAccountAndUserInfoEditForm.js';
import { BillingAccountAndUserInfoView } from './BillingAccountAndUserInfoView.js';
import { isSubscriptionEditable, validateSubscriptionForChangeOwner } from './BillingAccountUserInfoValidation.js';
import { PreventEditDialog } from './ScheduledChangeDialogs.js';
import { SubscriptionVersion } from './SubscriptionScheduledChangeDisclaimer.js';
import type { BillChannel } from '../../../generated/api/billChannel.js';
import type { BillingAccountsResponse } from '../../../generated/api/billingAccountsResponse.js';
import type { CompanyInfoResponse } from '../../../generated/api/companyInfoResponse.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { GetSubscriptionScheduledChanges } from '../../../generated/api/getSubscriptionScheduledChanges.js';
import type { Subscription } from '../../../generated/api/subscription.js';

import './BillingAccountAndUserInfoDetails.scss';

interface BillingAccountAndUserInfoDetailsProps {
  subscription: Subscription;
  hasPendingActions: boolean;
  companyInfo: CompanyInfoResponse;
  contacts: Contact[];
  billingAccounts: BillingAccountsResponse;
  billChannels: BillChannel[];
  subscriptionScheduledChanges?: GetSubscriptionScheduledChanges;
}

export const BillingAccountAndUserInfoDetails = ({
  subscription,
  billingAccounts,
  contacts,
  companyInfo,
  billChannels,
  hasPendingActions,
  subscriptionScheduledChanges,
}: BillingAccountAndUserInfoDetailsProps) => {
  const { authenticatedUser } = useAuth();
  const [isEditing, setEditing] = useState(false);
  const [showPreventEditDialog, setShowPreventEditDialog] = useState(false);

  const { scheduledChange, scheduledSubscription, subscriptionVersion, onSubscriptionVersionChange } =
    useScheduledChange(subscription, billingAccounts, contacts, subscriptionScheduledChanges);

  const isScheduledView = subscriptionVersion === SubscriptionVersion.SCHEDULED;
  const displayedSubscription = isScheduledView && scheduledSubscription ? scheduledSubscription : subscription;

  const isRingSubscription = subscription.subscriptionType === SubscriptionType.MOBILE_PBX_END_USER_SERVICE;
  const subscriptionValidationErrors = validateSubscriptionForChangeOwner(subscription);
  const isEditable = isSubscriptionEditable(subscription);

  const pbxType = subscription.details?.mobile?.pbxConfiguration?.pbxConfigurationDetails.pbxType;

  const onEditClick = () => {
    if (scheduledChange) {
      setShowPreventEditDialog(true);
      return;
    }
    if (isRingSubscription) {
      hasPendingActions
        ? openDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING })
        : openDialog({
            subscriptionId: subscription.subscriptionId,
            type: DialogType.RING_SUBSCRIPTION_UPDATE_USER,
          });
    } else if (subscription.subscriptionStatus === SubscriptionStatusType.SUSPENDED) {
      subscriptionSuspendedDialog(subscription);
    } else if (hasPendingActions) {
      subscriptionActionsPendingDialog();
    } else if (subscription.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      subscriptionInActivationDialog();
    } else {
      setEditing(true);
    }
  };

  return (
    <div className="of-billing-account-and-user-info-details">
      <HeaderWithEditButton
        title={t.CZ63(billingAccountAndUserMsg)}
        showButton={!isEditing && isEditable && !isExternalSubscription(subscription)}
        buttonText={t.NVPK(editMsg)}
        onClick={onEditClick}
      />

      {isEditing ? (
        <BillingAccountAndUserInfoEditForm
          subscription={subscription}
          companyInfo={companyInfo}
          contacts={contacts}
          billingAccounts={billingAccounts?.searchResults}
          billChannels={billChannels}
          purposeOfUseOrContact={createPurposeOfUseObj(subscription)}
          subscriptionValidationErrors={subscriptionValidationErrors}
          authenticatedUser={authenticatedUser}
          pbxType={pbxType}
          onCancel={() => setEditing(false)}
          onSuccess={() => setEditing(false)}
        />
      ) : (
        <BillingAccountAndUserInfoView
          subscription={displayedSubscription}
          originalSubscription={subscription}
          isScheduledView={isScheduledView}
          companyInfo={companyInfo}
          contacts={contacts}
          pbxType={pbxType}
          scheduledChange={scheduledChange}
          subscriptionVersion={subscriptionVersion}
          onSubscriptionVersionChange={onSubscriptionVersionChange}
        />
      )}

      {showPreventEditDialog && <PreventEditDialog onClose={() => setShowPreventEditDialog(false)} />}
    </div>
  );
};
